import {
  AdjustmentsHorizontalIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  DocumentCheckIcon,
  IdentificationIcon,
  ShieldExclamationIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { lazy } from "react";

import type { FeatureMetadata } from "../feature.types";
import {
  buildCrudPermissions,
  permissionId,
  createLazyPlaceholderPage,
} from "../shared/featureFactories";

const SchoolCalendarPage = lazy(() => import("./calendar/pages/SchoolCalendarPage"));
const EventsHolidaysPage = lazy(
  () => import("./eventsHolidays/pages/EventsHolidaysPage"),
);
const CertificatesPage = lazy(() => import("./certificates/pages/CertificatesPage"));
const SchoolProfilePage = lazy(() => import("./schoolProfile/pages/SchoolProfilePage"));
const SettingsPage = lazy(() => import("./settings/pages/SettingsPage"));

const configs = [
  {
    id: "id-cards",
    label: "ID Cards",
    path: "/id-cards",
    icon: IdentificationIcon,
    order: 4,
    permissions: buildCrudPermissions("Administration", "IDCardGenerator", {
      view: permissionId("Administration", "IDCardGenerator", "generate"),
      create: permissionId("Administration", "IDCardGenerator", "generate"),
      edit: permissionId("Administration", "IDCardGenerator", "generate"),
      delete: permissionId("Administration", "IDCardGenerator", "generate"),
    }),
  },
  {
    id: "visitor-management",
    label: "Visitor Management",
    path: "/visitor-management",
    icon: BuildingOffice2Icon,
    order: 5,
    permissions: buildCrudPermissions("Administration", "VisitorManagement"),
  },
  {
    id: "complaints",
    label: "Complaints & Grievance",
    path: "/complaints",
    icon: ShieldExclamationIcon,
    order: 6,
    permissions: buildCrudPermissions("Administration", "ComplaintGrievance"),
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  path: string;
  icon: FeatureMetadata["navigation"]["icon"];
  order: number;
  permissions: FeatureMetadata["permissions"];
}>;

const placeholderFeatures: FeatureMetadata[] = configs.map(
  ({ id, label, path, icon, order, permissions }) => ({
    id,
    moduleName: "administration",
    navigation: { label, path, icon, order },
    permissions,
    route: {
      path,
      element: createLazyPlaceholderPage(label),
    },
    readiness: "live",
  }),
);

const schoolCalendarFeature: FeatureMetadata = {
  id: "school-calendar",
  moduleName: "administration",
  navigation: {
    label: "School Calendar",
    path: "/administration/calendar",
    icon: CalendarDaysIcon,
    order: 1,
  },
  route: {
    path: "/administration/calendar",
    element: SchoolCalendarPage,
  },
  permissions: buildCrudPermissions("Administration", "SchoolCalendar"),
  readiness: "live",
};

const eventsHolidaysFeature: FeatureMetadata = {
  id: "events-holidays",
  moduleName: "administration",
  navigation: {
    label: "Events & Holidays",
    path: "/events-holidays",
    icon: SparklesIcon,
    order: 2,
  },
  route: {
    path: "/events-holidays",
    element: EventsHolidaysPage,
  },
  permissions: buildCrudPermissions("Administration", "EventsHolidays"),
  readiness: "live",
};

export const CertificatesFeature: FeatureMetadata = {
  id: "certificates",
  moduleName: "administration",
  navigation: {
    label: "Certificates",
    path: "/certificates",
    icon: DocumentCheckIcon,
    order: 3,
  },
  route: {
    path: "/certificates",
    element: CertificatesPage,
  },
  permissions: {
    ...buildCrudPermissions("Administration", "Certificates"),
    submitOnline: permissionId("Administration", "Certificates", "generate"),
  },
  readiness: "live",
};

const schoolProfileFeature: FeatureMetadata = {
  id: "school-profile",
  moduleName: "administration",
  navigation: {
    label: "School Profile",
    path: "/school-profile",
    icon: BuildingOffice2Icon,
    order: 7,
  },
  route: {
    path: "/school-profile",
    element: SchoolProfilePage,
  },
  permissions: buildCrudPermissions("UserAccessManagement", "SchoolProfile"),
  readiness: "live",
};

const settingsFeature: FeatureMetadata = {
  id: "settings",
  moduleName: "administration",
  navigation: {
    label: "Settings",
    path: "/settings",
    icon: AdjustmentsHorizontalIcon,
    order: 7,
  },
  route: {
    path: "/settings",
    element: SettingsPage,
  },
  permissions: buildCrudPermissions("Administration", "GeneralSettings", {
    view: permissionId("Administration", "GeneralSettings", "edit"),
    create: permissionId("Administration", "GeneralSettings", "edit"),
    edit: permissionId("Administration", "GeneralSettings", "edit"),
    delete: permissionId("Administration", "GeneralSettings", "edit"),
  }),
  readiness: "live",
};

export const ADMINISTRATION_FEATURES = [
  schoolCalendarFeature,
  eventsHolidaysFeature,
  CertificatesFeature,
  ...placeholderFeatures,
  schoolProfileFeature,
  settingsFeature,
] as const satisfies readonly FeatureMetadata[];
